'use es6';

import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import { getAppCategoryI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaRobots from 'marketplace-ui-common/components/elements/MetaRobots';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { FAILED, PENDING, SUCCEEDED, UNINITIALIZED } from 'marketplace-ui-common/requestStates/RequestState';
import { isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getBatchAppRecommendationTypes } from 'marketplace-ui-tracking';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NavMarker from 'react-rhumb/NavMarker';
import { useLocation } from 'react-router-dom';
import { marketplaceAppSearch } from '../../clients/marketplaceSearchClient';
import CanonicalLink from '../../components/elements/CanonicalLink';
import GenericError from '../../components/error/GenericError';
import ResultsPage from '../../components/storefront/results/ResultsPage';
import { DEFAULT_SEARCH_LENGTH } from '../../constants';
import * as FilterKey from '../../constants/search/FilterKey';
import { useAppContext } from '../../context/AppContext.tsx';
import StorefrontFiltersContext from '../../context/StorefrontFiltersContext';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import SearchResult from '../../records/SearchResult';
import { PublicOnly } from '../../selectors/auth';
import { getFullHref } from '../../utils/history';
import { useParsedPathParams } from '../../utils/hooks/useParsedPathParams';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
import { titlecase } from '../../utils/strings';
import { getAllCategoriesUrl, getPathPagination } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ALL_CATEGORIES_REGEX = new RegExp(`^${getAllCategoriesUrl()}$`);
const EMPTY_SEARCH_RESULT = new SearchResult();
const FailedComponent = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "STOREFRONT_ERROR_LOAD",
  children: /*#__PURE__*/_jsx(GenericError, {})
});
const SearchResults = ({
  match: {
    params,
    url
  }
}) => {
  const {
    page: activePage = 1
  } = useParsedPathParams(params);
  const {
    pathname
  } = useLocation();
  const {
    trackInteraction,
    trackPageView,
    trackFilterView,
    trackResultClick
  } = useContext(StorefrontUsageTrackerContext);
  const {
    filters,
    clearFilters,
    onPageChange,
    onSortChange,
    updateAllFilters,
    updateFilter
  } = useContext(StorefrontFiltersContext);
  const {
    [FilterKey.CATEGORY_V2]: categoryV2Value,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const [searchResult, setSearchResult] = useState(EMPTY_SEARCH_RESULT);
  const {
    items: searchResultItems,
    total: totalResults
  } = searchResult;
  const recommendationsCount = searchResultItems.filter(result => result.recommendation).length;
  const recommendationTypes = getBatchAppRecommendationTypes(searchResultItems);
  const isAllCategoriesHomepage = ALL_CATEGORIES_REGEX.test(pathname) && !searchValue && !filters.getValidFilterTagKeys().size && activePage === 1;
  const [shouldTrackPageView, setShouldTrackPageView] = useState(false);
  const [shouldTrackFilterView, setShouldTrackFilterView] = useState(false);
  const [requestState, setRequestState] = useState(UNINITIALIZED);
  const requestFulfilled = isFulfilled(requestState);
  const requestSucceeded = isSucceeded(requestState);
  const limit = DEFAULT_SEARCH_LENGTH;
  const [pageCount, setPageCount] = useState(0);
  const offset = (activePage - 1) * limit;
  const prevFilters = usePrevious(filters);
  const prevSearchValue = usePrevious(searchValue);
  const prevUrl = usePrevious(url);
  const getTrackedTotalResults = useCallback(() => {
    if (totalResults) {
      return totalResults;
    }
    return null;
  }, [totalResults]);
  const handleResultClick = useCallback((result, index, count) => {
    trackResultClick(result, index, count);
  }, [trackResultClick]);
  const referrer = useMemo(() => {
    let result;
    if (categoryV2Value.size === 1) {
      result = `category::${categoryV2Value.first()}`;
    }
    return result;
  }, [categoryV2Value]);
  const renderedResults = useMemo(() => {
    return /*#__PURE__*/_jsx(ResultsPage, {
      activePage: activePage,
      failedComponent: FailedComponent,
      filterable: true,
      filters: filters,
      isAllCategoriesHomepage: isAllCategoriesHomepage,
      offset: offset,
      onUpdateFilters: updateAllFilters,
      onClearFilters: clearFilters,
      onFilterChange: updateFilter,
      onPageChange: onPageChange,
      onResultClick: handleResultClick,
      onSortChange: onSortChange,
      referrer: referrer,
      requestState: requestState,
      searchResult: searchResult,
      sortable: true,
      trackInteraction: trackInteraction
    });
  }, [activePage, clearFilters, filters, handleResultClick, isAllCategoriesHomepage, offset, onPageChange, onSortChange, referrer, requestState, searchResult, trackInteraction, updateAllFilters, updateFilter]);
  const socialImageUrl = useSocialScreenshotUrl();
  const renderedPageMeta = useMemo(() => {
    const canonicalUrl = url;
    let hasNextPage = false;
    let hasPrevPage = false;
    let noIndexFollow = false;
    let metaDescription;
    let statusCode;
    if (requestSucceeded) {
      hasNextPage = activePage < pageCount;
      hasPrevPage = activePage > 1;
      if (searchValue) {
        noIndexFollow = true;
      } else if (!totalResults) {
        noIndexFollow = true;
        statusCode = 404;
      } else if (totalResults < 3) {
        noIndexFollow = true;
      }
    }
    if (categoryV2Value.size === 1) {
      metaDescription = unescapedText('appMarketplace.storefront.filters.category.metaDescription', {
        category: unescapedText(getAppCategoryI18nKey(categoryV2Value.first())).toLocaleLowerCase()
      });
    } else {
      metaDescription = unescapedText('appMarketplace.storefront.filters.category.items.all.metaDescription');
    }
    return /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: canonicalUrl
        }), hasPrevPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "prev",
          to: getPathPagination(url, activePage - 1)
        }), hasNextPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "next",
          to: getPathPagination(url, activePage + 1)
        }), /*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescription
        }), noIndexFollow && /*#__PURE__*/_jsx(MetaRobots, {
          directive: ['nofollow', 'noindex']
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: document.title
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: requestFulfilled,
          statusCode: statusCode
        })]
      })
    });
  }, [activePage, categoryV2Value, pageCount, requestFulfilled, requestSucceeded, searchValue, socialImageUrl, totalResults, url]);
  useEffect(() => {
    const count = totalResults || '';
    let documentTitle;
    if (categoryV2Value.size === 1) {
      documentTitle = I18n.text('appMarketplace.storefront.filters.category.htmlTitle', {
        count,
        category: titlecase(unescapedText(getAppCategoryI18nKey(categoryV2Value.first())))
      });
    } else {
      documentTitle = I18n.text('appMarketplace.storefront.filters.category.items.all.htmlTitle', {
        count
      });
    }
    document.title = documentTitle;
  }, [categoryV2Value, totalResults]);
  useEffect(() => {
    if (requestFulfilled && (shouldTrackPageView || shouldTrackFilterView)) {
      const trackedTotalResults = getTrackedTotalResults();
      const properties = {
        pageNumber: activePage,
        showRecommended: !!recommendationsCount,
        recommendationType: recommendationTypes,
        topRecommendationsCount: recommendationsCount,
        totalResults: trackedTotalResults !== null ? trackedTotalResults : undefined
      };
      if (shouldTrackPageView) {
        trackPageView(properties);
        setShouldTrackPageView(false);
      } else {
        trackFilterView(properties);
        setShouldTrackFilterView(false);
      }
      if (requestSucceeded && !trackedTotalResults) {
        trackInteraction('no results returned');
      }
    }
  }, [activePage, getTrackedTotalResults, recommendationsCount, recommendationTypes, requestFulfilled, requestSucceeded, shouldTrackFilterView, shouldTrackPageView, trackFilterView, trackInteraction, trackPageView]);
  useEffect(() => {
    if (url !== prevUrl || searchValue && searchValue !== prevSearchValue) {
      setShouldTrackPageView(true);
      setSearchResult(EMPTY_SEARCH_RESULT);
    }
  }, [prevSearchValue, prevUrl, searchValue, url]);
  useEffect(() => {
    if (url === prevUrl && searchValue === prevSearchValue && !filters.equals(prevFilters)) {
      setShouldTrackFilterView(true);
    }
  }, [filters, prevFilters, prevSearchValue, prevUrl, searchValue, url]);
  const language = getLang();
  const appContext = useAppContext();
  useEffect(() => {
    setRequestState(PENDING);
    marketplaceAppSearch({
      appContext
    }, {
      language,
      queries: [filters.toSearchQuery({
        length: limit,
        offset,
        searchSource: 'STANDARD'
      })],
      showPreferredLanguageFirst: filters.get(FilterKey.SHOW_PREFERRED_LANG_FIRST)
    }).then(response => {
      const newSearchResult = new SearchResult(response.results[0]);
      setSearchResult(newSearchResult);
      setPageCount(Math.ceil(newSearchResult.total / limit));
      setRequestState(SUCCEEDED);
    }).catch(error => {
      console.error(error);
      setSearchResult(EMPTY_SEARCH_RESULT);
      setPageCount(0);
      setRequestState(FAILED);
    });
  }, [appContext, filters, language, limit, offset]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [requestSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "STOREFRONT_LOAD"
    }), renderedResults, renderedPageMeta]
  });
};
SearchResults.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }),
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired
};
export default SearchResults;